import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from "react";

import { type SlowRpcAckRequest, useSlowRpcAckRequests } from "../rpc/requestLatencyState";
import {
  getCloudAuthFailureState,
  getWsConnectionStatus,
  getWsConnectionUiState,
  setBrowserOnlineStatus,
  type WsConnectionStatus,
  type WsConnectionUiState,
  useWsConnectionStatus,
  WS_RECONNECT_MAX_ATTEMPTS,
} from "../rpc/wsConnectionState";
import { toastManager } from "./ui/toast";
import { getPrimaryEnvironmentConnection } from "../environments/runtime";
import { resolveStartupReconnectVisibility } from "../startup/startupReadiness";

const FORCED_WS_RECONNECT_DEBOUNCE_MS = 5_000;
const RECOVERED_TOAST_MIN_VISIBLE_DISCONNECT_MS = 1_500;
const STABLE_CONNECTION_BEFORE_OUTAGE_TOAST_MS = 1_500;
type WsAutoReconnectTrigger = "focus" | "online" | "resume";

const connectionTimeFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  month: "short",
  second: "2-digit",
});

function formatConnectionMoment(isoDate: string | null): string | null {
  if (!isoDate) {
    return null;
  }

  return connectionTimeFormatter.format(new Date(isoDate));
}

function formatRetryCountdown(nextRetryAt: string, nowMs: number): string {
  const remainingMs = Math.max(0, new Date(nextRetryAt).getTime() - nowMs);
  return `${Math.max(1, Math.ceil(remainingMs / 1000))}s`;
}

function describeOfflineToast(): string {
  return "WebSocket disconnected. Waiting for network.";
}

function formatReconnectAttemptLabel(status: WsConnectionStatus): string {
  const reconnectAttempt = Math.max(
    1,
    Math.min(status.reconnectAttemptCount, WS_RECONNECT_MAX_ATTEMPTS),
  );
  return `Attempt ${reconnectAttempt}/${status.reconnectMaxAttempts}`;
}

function describeExhaustedToast(): string {
  return "Retries exhausted trying to reconnect";
}

function buildReconnectTitle(_status: WsConnectionStatus): string {
  return "Disconnected from Forge Server";
}

function describeRecoveredToast(
  previousDisconnectedAt: string | null,
  connectedAt: string | null,
): string {
  const reconnectedAtLabel = formatConnectionMoment(connectedAt);
  const disconnectedAtLabel = formatConnectionMoment(previousDisconnectedAt);

  if (disconnectedAtLabel && reconnectedAtLabel) {
    return `Disconnected at ${disconnectedAtLabel} and reconnected at ${reconnectedAtLabel}.`;
  }

  if (reconnectedAtLabel) {
    return `Connection restored at ${reconnectedAtLabel}.`;
  }

  return "Connection restored.";
}

function describeSlowRpcAckToast(requests: ReadonlyArray<SlowRpcAckRequest>): ReactNode {
  const count = requests.length;
  const thresholdSeconds = Math.round((requests[0]?.thresholdMs ?? 0) / 1000);

  return `${count} request${count === 1 ? "" : "s"} waiting longer than ${thresholdSeconds}s.`;
}

export const __formatConnectionMomentForTests = formatConnectionMoment;
export const __formatRetryCountdownForTests = formatRetryCountdown;
export const __describeOfflineToastForTests = describeOfflineToast;
export const __formatReconnectAttemptLabelForTests = formatReconnectAttemptLabel;
export const __describeExhaustedToastForTests = describeExhaustedToast;
export const __buildReconnectTitleForTests = buildReconnectTitle;
export const __describeRecoveredToastForTests = describeRecoveredToast;
export const __describeSlowRpcAckToastForTests = describeSlowRpcAckToast;

export function shouldAutoReconnect(
  status: WsConnectionStatus,
  trigger: WsAutoReconnectTrigger,
): boolean {
  // Suppress auto-reconnect when the cloud bearer session is expired —
  // retrying with a dead token just hammers the server with 401s.
  if (getCloudAuthFailureState().status === "expired") {
    return false;
  }

  // After OS sleep/wake the socket can be silently dead while phase is still
  // "connected" — focus/online would be no-ops. Reconnect unconditionally when
  // we've connected before; reconnect() harmlessly replaces a still-alive socket.
  if (trigger === "resume") {
    return status.hasConnected;
  }

  const uiState = getWsConnectionUiState(status);

  if (trigger === "online") {
    return (
      uiState === "offline" ||
      uiState === "reconnecting" ||
      uiState === "error" ||
      status.reconnectPhase === "exhausted"
    );
  }

  return (
    status.online &&
    status.hasConnected &&
    (uiState === "reconnecting" || status.reconnectPhase === "exhausted")
  );
}

export function shouldRestartStalledReconnect(
  status: WsConnectionStatus,
  expectedNextRetryAt: string,
): boolean {
  return (
    status.reconnectPhase === "waiting" &&
    status.nextRetryAt === expectedNextRetryAt &&
    status.online &&
    status.hasConnected
  );
}

export function shouldCloseRecoveredToastOnReset(
  currentToastId: ReturnType<typeof toastManager.add> | null,
  recoveredToastId: ReturnType<typeof toastManager.add>,
): boolean {
  return currentToastId === recoveredToastId;
}

export function shouldShowRecoveredToast(input: {
  readonly disconnectedToastShownAtMs: number | null;
  readonly hadStableConnectionBeforeDisconnect: boolean;
  readonly nowMs: number;
  readonly previousDisconnectedAt: string | null;
  readonly previousUiState: WsConnectionUiState;
}): boolean {
  if (
    !input.hadStableConnectionBeforeDisconnect ||
    input.previousDisconnectedAt === null ||
    (input.previousUiState !== "offline" && input.previousUiState !== "reconnecting") ||
    input.disconnectedToastShownAtMs === null
  ) {
    return false;
  }

  return (
    input.nowMs - input.disconnectedToastShownAtMs >= RECOVERED_TOAST_MIN_VISIBLE_DISCONNECT_MS
  );
}

export function WebSocketConnectionCoordinator() {
  const status = useWsConnectionStatus();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const lastForcedReconnectAtRef = useRef(0);
  const toastIdRef = useRef<ReturnType<typeof toastManager.add> | null>(null);
  const toastResetTimerRef = useRef<number | null>(null);
  const stableConnectionTimerRef = useRef<number | null>(null);
  const hadStableConnectionRef = useRef(false);
  const disconnectedToastShownAtRef = useRef<number | null>(null);
  const previousUiStateRef = useRef<WsConnectionUiState>(getWsConnectionUiState(status));
  const previousDisconnectedAtRef = useRef<string | null>(status.disconnectedAt);

  const runReconnect = useEffectEvent((showFailureToast: boolean) => {
    if (toastResetTimerRef.current !== null) {
      window.clearTimeout(toastResetTimerRef.current);
      toastResetTimerRef.current = null;
    }
    lastForcedReconnectAtRef.current = Date.now();
    void getPrimaryEnvironmentConnection()
      .reconnect()
      .catch((error) => {
        if (!showFailureToast) {
          console.warn("Automatic WebSocket reconnect failed", { error });
          return;
        }
        toastManager.add({
          type: "error",
          title: "Reconnect failed",
          description: error instanceof Error ? error.message : "Unable to restart the WebSocket.",
          data: {
            dismissAfterVisibleMs: 8_000,
            hideCopyButton: true,
          },
        });
      });
  });
  const syncBrowserOnlineStatus = useEffectEvent(() => {
    setBrowserOnlineStatus(navigator.onLine !== false);
  });
  const triggerManualReconnect = useEffectEvent(() => {
    runReconnect(true);
  });
  const triggerAutoReconnect = useEffectEvent((trigger: WsAutoReconnectTrigger) => {
    const currentStatus =
      trigger === "online" ? setBrowserOnlineStatus(true) : getWsConnectionStatus();

    if (!shouldAutoReconnect(currentStatus, trigger)) {
      return;
    }
    if (Date.now() - lastForcedReconnectAtRef.current < FORCED_WS_RECONNECT_DEBOUNCE_MS) {
      return;
    }

    runReconnect(false);
  });

  useEffect(() => {
    const handleOnline = () => {
      triggerAutoReconnect("online");
    };
    const handleFocus = () => {
      triggerAutoReconnect("focus");
    };

    syncBrowserOnlineStatus();
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", syncBrowserOnlineStatus);
    window.addEventListener("focus", handleFocus);

    const onSystemResume = window.desktopBridge?.onSystemResume;
    const unsubscribeResume =
      typeof onSystemResume === "function"
        ? onSystemResume(() => triggerAutoReconnect("resume"))
        : undefined;

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", syncBrowserOnlineStatus);
      window.removeEventListener("focus", handleFocus);
      unsubscribeResume?.();
    };
  }, []);

  useEffect(() => {
    if (status.reconnectPhase !== "waiting" || status.nextRetryAt === null) {
      return;
    }

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [status.nextRetryAt, status.reconnectPhase]);

  useEffect(() => {
    if (
      status.reconnectPhase !== "waiting" ||
      status.nextRetryAt === null ||
      !status.online ||
      !status.hasConnected
    ) {
      return;
    }

    const nextRetryAt = status.nextRetryAt;
    const timeoutMs = Math.max(0, new Date(nextRetryAt).getTime() - Date.now()) + 1_500;
    const timeoutId = window.setTimeout(() => {
      const currentStatus = getWsConnectionStatus();
      if (!shouldRestartStalledReconnect(currentStatus, nextRetryAt)) {
        return;
      }

      runReconnect(false);
    }, timeoutMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    status.hasConnected,
    status.nextRetryAt,
    status.online,
    status.reconnectAttemptCount,
    status.reconnectPhase,
  ]);

  useEffect(() => {
    const uiState = getWsConnectionUiState(status);
    const previousUiState = previousUiStateRef.current;
    const previousDisconnectedAt = previousDisconnectedAtRef.current;
    const hadStableConnectionBeforeDisconnect = hadStableConnectionRef.current;
    const reconnectVisibility = resolveStartupReconnectVisibility({
      hasConnected: status.hasConnected,
      stableConnectionSeen: hadStableConnectionBeforeDisconnect,
      reconnectPhase: status.reconnectPhase,
    });
    const shouldShowReconnectToast =
      reconnectVisibility === "actionable" && uiState === "reconnecting";
    const shouldShowOfflineToast =
      hadStableConnectionBeforeDisconnect &&
      uiState === "offline" &&
      status.disconnectedAt !== null;
    const shouldShowExhaustedToast =
      hadStableConnectionBeforeDisconnect && status.reconnectPhase === "exhausted";
    const shouldShowRecoveredToastNow =
      uiState === "connected" &&
      shouldShowRecoveredToast({
        disconnectedToastShownAtMs: disconnectedToastShownAtRef.current,
        hadStableConnectionBeforeDisconnect,
        nowMs: Date.now(),
        previousDisconnectedAt,
        previousUiState,
      });

    if (uiState === "connected") {
      if (!hadStableConnectionRef.current && stableConnectionTimerRef.current === null) {
        stableConnectionTimerRef.current = window.setTimeout(() => {
          hadStableConnectionRef.current = true;
          stableConnectionTimerRef.current = null;
        }, STABLE_CONNECTION_BEFORE_OUTAGE_TOAST_MS);
      }
    } else if (!hadStableConnectionRef.current && stableConnectionTimerRef.current !== null) {
      window.clearTimeout(stableConnectionTimerRef.current);
      stableConnectionTimerRef.current = null;
    }

    if (
      toastResetTimerRef.current !== null &&
      (shouldShowReconnectToast || shouldShowOfflineToast || shouldShowExhaustedToast)
    ) {
      window.clearTimeout(toastResetTimerRef.current);
      toastResetTimerRef.current = null;
    }

    if (shouldShowReconnectToast || shouldShowOfflineToast || shouldShowExhaustedToast) {
      const toastPayload = shouldShowOfflineToast
        ? {
            description: describeOfflineToast(),
            timeout: 0,
            title: "Offline",
            type: "warning" as const,
            data: {
              hideCopyButton: true,
            },
          }
        : shouldShowExhaustedToast
          ? {
              actionProps: {
                children: "Retry",
                onClick: triggerManualReconnect,
              },
              description: describeExhaustedToast(),
              timeout: 0,
              title: "Disconnected from Forge Server",
              type: "error" as const,
              data: {
                hideCopyButton: true,
              },
            }
          : {
              actionProps: {
                children: "Retry now",
                onClick: triggerManualReconnect,
              },
              description:
                status.nextRetryAt === null
                  ? `Reconnecting... ${formatReconnectAttemptLabel(status)}`
                  : `Reconnecting in ${formatRetryCountdown(status.nextRetryAt, nowMs)}... ${formatReconnectAttemptLabel(status)}`,
              timeout: 0,
              title: buildReconnectTitle(status),
              type: "loading" as const,
              data: {
                hideCopyButton: true,
              },
            };

      if (toastIdRef.current) {
        toastManager.update(toastIdRef.current, toastPayload);
      } else {
        toastIdRef.current = toastManager.add(toastPayload);
      }
      disconnectedToastShownAtRef.current ??= Date.now();
    } else if (toastIdRef.current && !shouldShowRecoveredToastNow) {
      toastManager.close(toastIdRef.current);
      toastIdRef.current = null;
      disconnectedToastShownAtRef.current = null;
    }

    if (shouldShowRecoveredToastNow) {
      const successToast = {
        description: describeRecoveredToast(previousDisconnectedAt, status.connectedAt),
        title: "Reconnected to Forge Server",
        type: "success" as const,
        timeout: 0,
        data: {
          dismissAfterVisibleMs: 8_000,
          hideCopyButton: true,
        },
      };

      if (toastIdRef.current) {
        toastManager.update(toastIdRef.current, successToast);
      } else {
        toastIdRef.current = toastManager.add(successToast);
      }

      const recoveredToastId = toastIdRef.current;
      disconnectedToastShownAtRef.current = null;
      toastResetTimerRef.current = window.setTimeout(() => {
        if (shouldCloseRecoveredToastOnReset(toastIdRef.current, recoveredToastId)) {
          toastManager.close(recoveredToastId);
          toastIdRef.current = null;
        }
        toastResetTimerRef.current = null;
      }, 8_250);
    }

    previousUiStateRef.current = uiState;
    previousDisconnectedAtRef.current = status.disconnectedAt;
  }, [nowMs, status]);

  useEffect(() => {
    return () => {
      if (toastResetTimerRef.current !== null) {
        window.clearTimeout(toastResetTimerRef.current);
      }
      if (stableConnectionTimerRef.current !== null) {
        window.clearTimeout(stableConnectionTimerRef.current);
      }
    };
  }, []);

  return null;
}

export function SlowRpcAckToastCoordinator() {
  const slowRequests = useSlowRpcAckRequests();
  const status = useWsConnectionStatus();
  const toastIdRef = useRef<ReturnType<typeof toastManager.add> | null>(null);

  useEffect(() => {
    if (getWsConnectionUiState(status) !== "connected") {
      if (toastIdRef.current) {
        toastManager.close(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }

    if (slowRequests.length === 0) {
      if (toastIdRef.current) {
        toastManager.close(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }

    const nextToast = {
      description: describeSlowRpcAckToast(slowRequests),
      timeout: 0,
      title: "Some requests are slow",
      type: "warning" as const,
    };

    if (toastIdRef.current) {
      toastManager.update(toastIdRef.current, nextToast);
    } else {
      toastIdRef.current = toastManager.add(nextToast);
    }
  }, [slowRequests, status]);

  return null;
}

export function WebSocketConnectionSurface({ children }: { readonly children: ReactNode }) {
  return children;
}
